import * as React from "react";

import "./product-options-step.scss";

import {
    ReactGATemplateEvent,
    setGAUserProperties
}                                       from "@Common/libs/ga/xd-react-ga";
import {isLegacyItem}                   from "@Common/logic/item/is-legacy-item";
import {to_isAddressable}               from "@Common/logic/template-options/template-options-is-addressable";
import {isKit, isPickPack, isUyoMPB}    from "@Common/logic/template-options/template-options-pickpack-data-logic";
import {cs}                             from "@Common/react/chain-services";
import {consumeContext, provideContext} from "@Common/react/context";
import {Invoke}                         from "@Common/react/invoke";
import {Load2}                          from "@Common/react/load2";
import {UseState}                       from "@Common/react/use-state";
import {BugBountyAlert}                 from "@Common/ui-components/bug-bounty-alert/bug-bounty-alert";
import {ItemOptions}                    from "@Common/ui-components/heavy/item-options/item-options";
import {Responsive}                     from "@Common/ui-components/responsive";
import {setNestedValue}                 from "@Common/utils/arr-path";
import {chain}                          from "@Common/utils/fs";
import {deepMerge}                      from "@Common/utils/objects";
import {parseUrlQuery}                  from "@Common/utils/parse-url-query";

import {PageShow}               from "../../../common/components/page-show";
import {ProductCustomizeStatus} from "../../../common/product-customize-status/product-customize-status";
import {CustomizeLayout}        from "../common/customize-layout/customize-layout";
import {loadMailingLists}       from "../common/load-mailing-lists";

import {CustomizeActionNew}            from "./customize-action/customize-action-new";
import {EmulateAgentOfOrder}           from "./loaders/emulate-agent-of-order";
import {loadItem}                      from "./loaders/load-item";
import {loadTemplateBack}              from "./loaders/load-template-back";
import {loadTemplateOptions}           from "./loaders/load-template-options";
import {deliveryMethodsModules}        from "./modules/delivery-methods/delivery-methods-modules";
import {downloadDeliveryModules}       from "./modules/download-delivery/download-delivery-modules";
import {pickupAddressModules}          from "./modules/pickup-address/pickup-address-modules";
import {itemNameModules}               from "./modules/item-name/item-name-modules";
import {lineItemsModules}              from "./modules/line-items/line-items-modules";
import {listingTypeModules}            from "./modules/listing-type/listing-type-modules";
import {mailingClassModules}           from "./modules/mailing-class/mailing-class-modules";
import {OIA}                           from "./modules/oia/oia";
import {paperTypeModules}              from "./modules/paper-type/paper-type-modules";
import {pdfDownloadModules}            from "./modules/pdf-download/pdf-download-modules";
import {pickPackModules}               from "./modules/pick-pack/pick-pack-modules";
import {quantityModules}               from "./modules/quantity/quantity-modules";
import {UyoModal}                      from "./modules/uyo/uyo-modal/uyo-modal";
import {UyoModules}                    from "./modules/uyo/uyo-modules";
import {variationsModules}             from "./modules/variations/variations-modules";
import {yardSignModules}               from "./modules/yard-sign/yard-sign-modules";
import {OrderEditor}                   from "./order-editor/order-editor";
import {TemplateSelectionTemplateView} from "./template-selection-template-view/template-selection-template-view";

const GALLERY_SIZE = {
    DESKTOP: 6,
    MOBILE: 4
}

export const ProductOptionsStep = ({match, location}) => cs(
    ["query", (_, next) => next(parseUrlQuery(location.search))],
    consumeContext("apis"),
    consumeContext("company"),
    consumeContext("systemConstants"),
    consumeContext("userLevels"),
    ["isInternalUser", ({userLevels}, next) => next(userLevels?.internalUser)],
    ["itemId", (_, next) => next(match.params.item)],
    ["item", ({itemId}, next) => itemId ? (
        loadItem({itemId, next})
    ) : (
        next()
    )],
    // @ts-ignore
    ["responsive", (_, next) => <Responsive {...{
        sizes: {
            md: 1024,
        },
        next,
    }}/>],
    ["view", (_, next) => UseState({
        initValue: {enlarged: false, key: 0},
        next,
    })],
    ["gallery", ({responsive}, next) => UseState({
        initValue: {
            start: 0,
            end: responsive.ge("md") ? GALLERY_SIZE.DESKTOP - 1 : GALLERY_SIZE.MOBILE - 1,
            size: responsive.ge("md") ? GALLERY_SIZE.DESKTOP : GALLERY_SIZE.MOBILE
        },
        next,
    })],
    ({itemId}, next) => itemId ? (
        Invoke({
            action: () => setGAUserProperties({idOrder: itemId}),
            next,
        })
    ) : (
        next()
    ),
    ["oia", ({item}, next) => OIA({item, next})],
    ({item}, next) => item ? (
        EmulateAgentOfOrder({item, next})
    ) : (
        next()
    ),
    ["templateId", ({item}, next) => next(item ? item.sku.front : match.params.product_id)],
    ["idUserCreatedTemplate", ({item}, next) => next(item ? item.sku.userCreatedTemplate : match.params.idUserCreatedTemplate)],
    ["templateBack", ({templateId}, next) => templateId && loadTemplateBack({templateId, next})],
    ["templateBackId", ({templateId, templateBack, item}, next) => templateBack && UseState({
        initValue: item ? parseInt(item.sku.back) : templateBack?.backs[0]?.id ?? templateId,
        next,
    })],
    ["pageShow", (_, next) => <PageShow{...{next}}/>],
    ["customizeStatus", ({pageShow}, next) => ProductCustomizeStatus({pageShow, next})],
    ["sku", ({templateId, templateBackId, idUserCreatedTemplate}, next) => next(
        {
            front: Number(templateId),
            ...templateBackId.value ? {back: Number(templateBackId.value)} : {},
            ...idUserCreatedTemplate ? {userCreatedTemplate: Number(idUserCreatedTemplate)} : {}
        }
    )],
    ["templateOptions", ({sku}, next) => loadTemplateOptions({
        sku,
        next,
    })],
    ({templateOptions}, next) => provideContext({templateOptions}, next),
    ({templateOptions}, next) => (
        Invoke({
            action: () => ReactGATemplateEvent({eventName: "view_item", templateOptions}),
            next,
        })
    ),
    ({}, next) => loadMailingLists({next}),
    ["uyoModal", ({templateOptions, item, view, gallery, customizeStatus}, next) => UyoModal({templateOptions, file: item?.metaData.pdfValidation, item, view, gallery, customizeStatus, next})],
    ["itemOptions", ({templateBack, templateOptions, item, view, gallery, customizeStatus, oia}, next) => ItemOptions({
        templateOptions,
        item: !item ? item : {
            ...clearLegacyItemData(item),
            result: item.result,
        },
        extraModules: [
            ...deliveryMethodsModules({templateOptions, item}),

            {
                name: "mailingListIds",
                load: ({}, next) => next(item?.selections?.mailingListIds?.map((ml) => ml.id) ?? []),
            },
            ...mailingClassModules({templateOptions, item}),
            ...(!oia && isUyoMPB(templateOptions) ? UyoModules({templateOptions, item, view, gallery, customizeStatus}) : []),
            ...downloadDeliveryModules({}),
            ...pickupAddressModules({}),
            ...quantityModules({}),
            ...pdfDownloadModules({}),
            ...paperTypeModules({templateOptions}),
            ...itemNameModules({
                item,
                getDefaultValue: () => sessionStorage.getItem("listingStreetAddress") || templateOptions?.name,
            }),
            ...lineItemsModules({item, templateOptions}),
            ...pickPackModules({item, templateOptions}),
            ...yardSignModules({item, templateOptions}),
            ...listingTypeModules({
                item, templateOptions,
            }),
            ...variationsModules({item, templateBack, templateOptions}),
        ],
        next,
    })],
    ["customizeFlow", ({templateOptions, oia, query, itemOptions}, next) => next(
        [
            {},
            to_isAddressable(templateOptions) && {
                needsDistributionOptions: true, // mailing || shipping + addressable
            },
            (oia || isPickPack(templateOptions) || isUyoMPB(templateOptions) || !getMaxValueForIsProof(itemOptions)) && {
                legacy: {
                    xpressoCheckout: true,
                },
            },
            (templateOptions.proofEngineType !== 0) && {
                legacy: {},
            },
            query?.id_cart === 'new' && {
                afterProofApproval: true,
            },
        ]
            .filter(v=>v)
            .reduce(deepMerge)
    )],
    ["specifications", ({apis, templateOptions}, next) => Load2({
        fetch: async () => await apis.next.order.getTemplateSpecFields(templateOptions?.sku),
        next,
    })],
    ["build3BugBountyData", ({apis}, next) => Load2({
        fetch: async () => await apis.next.bugBounty.getBugBountyProgramData(),
        next,
    })],
    ["build3SupportedBuildPaths", ({systemConstants}, next) => next(JSON.parse(systemConstants?.value?.BUILD3_SUPPORTED_BUILDPATHS))],
    ["build3Cond", ({templateOptions, build3SupportedBuildPaths}, next) => next(
        !!templateOptions.build_path && !build3SupportedBuildPaths.includes(templateOptions?.build_path)
    )],
    ["showBugBountyAlert", ({templateOptions, isInternalUser, build3BugBountyData, build3SupportedBuildPaths}, next) => next(
        templateOptions?.proofEngineType === 7
        && isInternalUser
        && !!templateOptions.build_path
        && !build3SupportedBuildPaths.includes(templateOptions?.build_path)
        && build3BugBountyData.value?.isActive
    )],
    ["forceBuild3", ({}, next) => UseState({
        initValue: false,
        next,
    })],
    ["prepareSave", ( {oia, itemOptions}, next) => next(
        (item) => chain(item,
            clearLegacyItemData,
            oia ? (
                (item) => setNestedValue(item, ["metaData", "oiaProofs"], oia.proofs)
            ) : (
                v=>v
            ),
            itemOptions.itemEditor.prepareSave,
        ),
    )],
    ["customizeAction", (
        {
            sku,
            templateOptions,
            customizeFlow,
            customizeStatus,
            item,
            query,
            build3Cond,
            forceBuild3,
            specifications,
            prepareSave,
        },
        next
    ) => CustomizeActionNew({
        sku,
        customizeFlow,
        templateOptions,
        status: customizeStatus,
        item,
        prepareSave,
        location,
        isFromApproval: isFromApproval(query),
        build3Cond: build3Cond,
        forceBuild3,
        next,
    })],
    ["templateSelectionTemplateView", ({
        templateBack,
        templateBackId,
        item,
        itemOptions,
        query,
        view,
        gallery,
        oia,
        templateOptions,
    }, next) => templateBack && TemplateSelectionTemplateView({
        templateOptions,
        templateBack,
        selected: templateBackId,
        isFromApproval: isFromApproval(query),
        item,
        itemOptions,
        view,
        gallery,
        oia,
        next,
    })],
    ({templateOptions,item,oia,query, itemOptions}, next) => CustomizeLayout({
        active: "product-options",
        back: oia || isFromApproval(query) ? null : templateOptions?.userCreatedCategories ? {
            link: `/my-templates/${templateOptions.userCreatedCategories.id}`,
            label: `Back to ${templateOptions.userCreatedCategories.name}`,
        } : {
            link: (templateOptions.categories.frontTemplate.id && templateOptions.subCategories.frontTemplate.id) ? (
                `/cat/${templateOptions.categories.frontTemplate.id}/sub/${templateOptions.subCategories.frontTemplate.id}`
            ) : (
                ""
            ),
            label: (!templateOptions.categories.frontTemplate.id || !templateOptions.subCategories.frontTemplate.name) ? (
                "Back"
            ) : (
                `Back to ${templateOptions.subCategories.frontTemplate.name}`
            ),
        },
        customizable: !oia && !isPickPack(templateOptions) && !isUyoMPB(templateOptions) && !!getMaxValueForIsProof(itemOptions),
        addressable: to_isAddressable(templateOptions),
        item,
        isKit: isKit(templateOptions),
        disabled: ["checkout", "customization", "distribution-options", "kit-options"],
        isApprovalAfterProof: templateOptions.location === "proof",
        next,
    }),
    ({
        item,
        templateOptions,
        templateBack,
        customizeAction,
        customizeStatus,
        templateBackId,
        templateSelectionTemplateView,
        oia,
        customizeFlow,
        itemOptions,
        query,
        uyoModal,
        prepareSave,
        showBugBountyAlert,
        forceBuild3,
        build3BugBountyData
    }) => (
        <div className="product-options-step-6rz">
            {cs(
                (_, next) => (
                    oia && !customizeStatus.active && isUyoMPB(templateOptions) && item?.metaData.pdfValidation ? (<>
                        {next()}
                        {!item?.metaData.pdfValidation.thumbnails && Invoke({action: () => uyoModal.show({prepareSave}), next})}
                    </>) : !customizeStatus.active ? (
                        next()
                    ) : (
                        <div className="center">
                            {customizeStatus.message}
                        </div>
                    )
                ),
                ({}) => (<>
                    {showBugBountyAlert && <BugBountyAlert
                        title={build3BugBountyData.value.title}
                        description={build3BugBountyData.value.description}
                        imageUrl={build3BugBountyData.value.imageUrl}
                        documentationLink={build3BugBountyData.value.documentationLink}
                        buttonType={build3BugBountyData.value.buttonType}
                        buttonText={build3BugBountyData.value.buttonText}
                        buttonLink={build3BugBountyData.value.buttonLink}
                        buttonChangeHandler={(val) => {forceBuild3.onChange(val)}}
                        location={build3BugBountyData.value.location}
                        buildpath={templateOptions?.build_path ?? "none"}
                    />}
                    <div className="content">
                        <div className="left">
                            {templateSelectionTemplateView?.render()}
                        </div>
                        <div className="right">
                            {OrderEditor({
                                templateOptions, templateBack, customizeAction,
                                templateBackId: templateBackId.value,
                                oia,
                                customizeFlow,
                                itemOptions,
                                isFromApproval: isFromApproval(query),
                                prepareSave,
                                item
                            })}
                        </div>
                    </div>
                    </>),
            )}
        </div>
    )
);

const clearLegacyItemData = (item) => (
    (!item || !isLegacyItem(item)) ? (
        item
    ) : {
        itemId: item.itemId,
        itemName: item.itemName,
        metaData: item.metaData,
        selections: item.selections,
        tracking: item.tracking,
    }
);

// noinspection JSUnusedLocalSymbols
const sampleCustomizeFlow = {
    needsDistributionOptions: true, // mailing || shipping + addressable
    legacy: { // !isCCTemplate
        xpressoCheckout: {  // oia
            forceIgnoreDistributionOptions: true, // isPickPack
        },
    },
};

const isFromApproval = (query) => {return query?.referrer === "approval-requests"}

export const getMaxValueForIsProof = (itemOptions) => {
    const matchingVariations = itemOptions?.variationSelectionState?.selection?.matchingVariations;
    if(matchingVariations?.length > 0) {
        return Math.max(...matchingVariations.map(
            variation => parseInt(variation.product.doesProof)
        ));
    } else {
        return 1;
    }
};
